import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react'
import { useReducedMotion } from 'motion/react'
import { ArrowLeft, Map as MapIcon, Maximize2, Video, VideoOff, VolumeOff, Wifi } from 'lucide-react'
import { WarmUpMark } from '@/components/brand/WarmUpMark'
import { growthFrom } from '@/components/map/camera-sphere'
import { Badge, Button, EmptyState, Tooltip } from '@/components/play'
import type { EventPhase } from '@/lib/watch/rules'
import type { PublicProfile, WatchCamera, WatchConnectionState } from '@/lib/watch/types'
import { cn } from '@/lib/utils'
import { HostAvatar } from './HostAvatar'
import { LiveTrack } from './LiveTrack'
import { SourceGlyph } from './SourceGlyph'
import { connectionWait, quietCanvas, quietWords, type CameraCount, type QuietCanvas } from './noCameras'
import { StackedGlyph, StateGlyph, WaitingState, type GlyphMotion } from './StateGlyph'
import './watch.css'

/** The room's connection as the canvas shows it, or `stopped`: nothing is trying again (the event's video was refused). */
export type CanvasConnection = WatchConnectionState | 'stopped'

/** Where a jump into a camera starts: its sphere's frame on the map, in viewport pixels, and that frame's corner radius. */
export interface JumpOrigin {
  left: number
  top: number
  width: number
  height: number
  radius: number
}

/** A camera opened from the full-size map (a sphere): the canvas turns to it, growing it out of `from` when known. */
export interface CanvasJump {
  from: JumpOrigin | null
}

/** How long the controls (speaker, full screen) stay after the last touch or pointer movement over the video, as a player's do. */
export const CONTROLS_AWAKE_MS = 3000

/** The theme's large motion duration in milliseconds, as `element` resolves it (0 under reduced motion, or unresolved). */
function largeDuration(element: Element | null) {
  const value = element ? getComputedStyle(element).getPropertyValue('--motion-large-duration').trim() : ''
  const match = /^(\d*\.?\d+)(ms|s)$/.exec(value)
  return match ? Number(match[1]) * (match[2] === 's' ? 1000 : 1) : 0
}

/** Where a jump starts, read from the sphere's frame as it opens (before the map moves); null without a laid-out frame. */
export function jumpOrigin(frame: Element | null): JumpOrigin | null {
  if (!frame) return null
  const { left, top, width, height } = frame.getBoundingClientRect()
  if (!width || !height) return null
  const style = getComputedStyle(frame)
  const radius = parseFloat(style.borderTopLeftRadius) || parseFloat(style.borderRadius) || 0
  return { left, top, width, height, radius }
}

/**
 * The swap card's button over the canvas that holds `node` (the map, moved into the canvas's layer), or null: what
 * covers the full-size map's bottom-right corner.
 */
export function swapCardOver(node: Element | null) {
  return node?.closest('.watch-canvas')?.querySelector<HTMLElement>(':scope > .watch-canvas__swap') ?? null
}

export interface WatchCanvasProps {
  /** What fills the canvas; the other one sits in the swap card. */
  mode: 'camera' | 'map'
  /**
   * The camera the canvas shows. Kept while it drops (`online: false`), so it says Offline under its name; on its way
   * (`waiting`), the canvas waits for it with the camera waking on the stacked disc and no words.
   */
  watched: WatchCamera | null
  /**
   * The event's cameras and Live shows, online or not (including those `live_only` hides). With nothing to show it tells
   * "No cameras here" (none) from warming up (some, none live), and a Live show alone from a camera. Required, so the
   * page can't leave it out.
   */
  cameraCount: CameraCount
  /**
   * Where the event is in its schedule (`useEventPhase`; `before` when left out): with nothing live the canvas warms up
   * before the start, calls a lull after it a quiet stretch, and says all finished once the scheduled end has passed.
   */
  phase?: EventPhase
  /**
   * The room's connection (connected when left out). Until it connects the canvas is tuning in; while the viewer's own
   * connection reconnects, or a lost room tries again, tuning back in, without a camera's name (the camera didn't drop);
   * `stopped`, a still glyph alone (the page says why and offers Try again). None shows a picture.
   */
  connection?: CanvasConnection
  /** The host's called camera. Back to host shows only while there is one. */
  calledIdentity: string | null
  /**
   * The course map. The page passes `interactive={false}` while it sits in the swap card. Left out (the phone layouts,
   * where the map has its own tab), there is no swap card: the camera fills the canvas and Back to host sits at its
   * foot, bottom right.
   */
  map?: ReactNode
  /**
   * The map draws the event's course (a route). The swap card's label then says Course and its button "Show the course
   * map"; without one (a map of the cameras alone, or nothing to frame yet) they say Map and "Show the map".
   */
  course?: boolean
  /** The stage's talking heads, bottom left. Leave it out while nobody is on the stage, so the foot stays empty. */
  faces?: ReactNode
  /**
   * The viewer's speaker (`SoundControl`), bottom right: above the swap card, or in the corner without one. With
   * `fullscreen` it is the canvas's controls, which show while the viewer is at the video, as a player's: for
   * `CONTROLS_AWAKE_MS` after a touch or a pointer moving over the video or the controls' spot (in map mode, not the
   * rest of the map), at once gone when a mouse leaves the canvas, and kept while hovered, focused from the keyboard or
   * dragged.
   */
  sound?: ReactNode
  /** Full screen (`FullscreenToggle`), right of the speaker, the corner's own: left out where the browser can't. */
  fullscreen?: ReactNode
  /**
   * The stage's host turned the watched source right off (docs/features/stage-volume.md): a struck-through speaker
   * follows its name in the caption, so silence under the faces doesn't read as broken sound.
   */
  sourceOff?: boolean
  /** The viewer picked a camera other than the host's call. */
  overridden: boolean
  host: PublicProfile
  onSwap(): void
  onBackToHost(): void
  /** Beside the camera's name: the host's Preview marker. */
  label?: ReactNode
  /**
   * The camera was jumped into from the full-size map: pass a new object with `mode="camera"` and the camera that
   * opened as `watched`. The camera grows out of the sphere over the map to fill the canvas, from a box of the canvas's
   * shape centred on the sphere's frame (so the picture never squashes; the large curve's duration; at once under
   * reduced motion or without a frame), then the jump settles: the map goes into the swap card as in any swap, and focus
   * left on the map moves onto the swap button, the way back to the map. Escape goes back too (`onSwap`), while focus is
   * on the canvas or nowhere, unless something else used the key first (a zoomed face, an open menu). Clear it once the
   * canvas leaves the camera, or once it shows another camera.
   */
  jump?: CanvasJump | null
  /**
   * The jump settled (passed back): the camera fills the canvas and the map is in the swap card. Until then the map
   * keeps the canvas under the growing camera as the viewer left it, so the page keeps it as it was (interactive).
   */
  onJumpSettled?(jump: CanvasJump): void
  /**
   * Shrunk to a small player over a map filling the screen (a phone's full map, or the map on its side): the picture
   * alone, a wait's glyph without its words, and one button over it, named `label`, that brings the video back
   * (`onOpen`). The caption, Back to host, the faces and the speaker stay mounted out of sight (watch.css), so nothing
   * that plays restarts. Still the same element: the page's stylesheet moves it, so the video never reloads.
   */
  pip?: { label: string; onOpen(): void } | null
  /**
   * The stage's broadcast elements over the picture, camera or map (`BroadcastLayer`): above the main layer, under the
   * top row, the faces and the swap card. Out of sight in the small player.
   */
  broadcast?: ReactNode
  /** The stage's reactions floating up (`ReactionLayer`): over the picture and broadcast, under the faces and controls. */
  reactions?: ReactNode
  className?: string
}

type Slot = 'main' | 'card'

/**
 * The watch screen's canvas: the watched camera full-bleed with its live badge and name, the course map in the
 * swap card bottom right (or the other way round), the faces bottom left, the speaker above the card and Back to host
 * at the top.
 *
 * The map and the camera each keep one place in the tree and only change slot, so a swap never remounts the map
 * (Mapbox) or the video; CSS moves them and fades them in from the theme's popup scale. The swap card's button is
 * one persistent button over the card, beside both layers, so a swap only renames it and focus stays put. A jump
 * (`jump`: a sphere opened on the full-size map) is a swap whose camera grows out of the sphere instead.
 */
export function WatchCanvas({
  mode: asked,
  watched,
  cameraCount,
  phase = 'before',
  connection = 'connected',
  calledIdentity,
  map,
  course = false,
  faces,
  sound,
  fullscreen,
  sourceOff = false,
  overridden,
  host,
  onSwap,
  onBackToHost,
  label,
  jump = null,
  onJumpSettled,
  pip = null,
  broadcast,
  reactions,
  className,
}: WatchCanvasProps) {
  const hasCard = map !== undefined
  const mode = hasCard ? asked : 'camera'
  const reducedMotion = useReducedMotion()
  // Animate swaps, not the first paint, and not the layout adding or taking away the card (which can force the mode).
  const [shown, setShown] = useState({ mode, hasCard })
  const [swapped, setSwapped] = useState(false)
  if (shown.mode !== mode || shown.hasCard !== hasCard) {
    setShown({ mode, hasCard })
    setSwapped(shown.hasCard === hasCard)
  }

  const cameraSlot: Slot = mode === 'camera' ? 'main' : 'card'
  const live = Boolean(watched?.online) && connection === 'connected'
  const hostsPick = live && watched?.identity === calledIdentity
  const hasLabel = label !== undefined && label !== null && label !== false
  const showCaption = mode === 'camera' && watched !== null && (live || hasLabel)
  const showBack = overridden && calledIdentity !== null
  const hasFaces = faces !== undefined && faces !== null && faces !== false
  // Something sits at the foot (the swap card or faces): a narrow canvas's wait centres above
  // it. With nothing there it centres in the whole canvas.
  const footed = hasCard || hasFaces
  // With no camera to watch, the card's camera side is the video area (it may have no camera at all): never "the camera".
  const swapLabel = mode === 'camera' ? (course ? 'Show the course map' : 'Show the map') : watched ? `Show ${watched.name} full size` : 'Show the video area'

  // Back to host goes once used (or once the host calls this camera). If it had focus, that would drop to the page;
  // keep it in the canvas, on the swap button. React sends no blur for an unmounted button, so the flag survives.
  // Without a swap card (the phone layouts) the canvas has nothing else to focus, and it falls to the page.
  const swapRef = useRef<HTMLButtonElement>(null)
  const backHasFocus = useRef(false)
  useLayoutEffect(() => {
    if (showBack || !backHasFocus.current) return
    backHasFocus.current = false
    const active = document.activeElement
    if (active === null || active === document.body) swapRef.current?.focus({ preventScroll: true })
  }, [showBack])

  // A jump into a camera, while the canvas shows it (with a swap card to go back to).
  const rootRef = useRef<HTMLDivElement>(null)
  const mapLayerRef = useRef<HTMLDivElement>(null)
  const facesRef = useRef<HTMLDivElement>(null)
  const jumped = hasCard && mode === 'camera' ? jump : null
  // The camera's layer starts at a box of its own shape around the sphere's frame (`growthFrom`): the offset and one
  // scale from the canvas's box (the layer's, at rest), and the frame's radius undone by that scale, read once per jump
  // before it paints. A circle's box is a pill around it.
  const [growth, setGrowth] = useState<{ jump: CanvasJump; style: CSSProperties } | null>(null)
  // Until the jump settles, the map keeps the whole canvas under the camera, as the viewer left it.
  const [settledJump, setSettledJump] = useState<CanvasJump | null>(null)
  const holding = jumped !== null && settledJump !== jumped
  const grown = jumped !== null && growth?.jump === jumped
  const grow = holding && grown ? growth?.style : undefined
  const mapSlot: Slot = mode === 'map' || holding ? 'main' : 'card'
  // The controls wake for a while after the viewer touches or points at the video, or at the controls' own spot, never
  // for the map under a pointer that pans it. Asleep, CSS keeps them out of the way and out of reach (a tap where they
  // were wakes them, never mutes), so over the full-size map their spot is the map's: the pointer's place tells.
  const hasControls = Boolean(sound || fullscreen)
  const [controlsAwake, setControlsAwake] = useState(false)
  const controlsRef = useRef<HTMLDivElement>(null)
  // Each wake starts the wait again, in a timer rather than state, so a moving pointer never renders the canvas.
  const controlsTimer = useRef<number | undefined>(undefined)
  const wakeRef = useRef<(event: PointerEvent) => void>(() => {})
  useLayoutEffect(() => {
    wakeRef.current = ({ target, clientX, clientY }) => {
      if (!hasControls) return
      if (mapSlot === 'main' && target instanceof Node && mapLayerRef.current?.contains(target)) {
        const spot = controlsRef.current?.getBoundingClientRect()
        // No box at all (not laid out: the small player's canvas hides it) is nowhere.
        if (!spot?.width || clientX < spot.left || clientX > spot.right || clientY < spot.top || clientY > spot.bottom) return
      }
      setControlsAwake(true)
      window.clearTimeout(controlsTimer.current)
      controlsTimer.current = window.setTimeout(() => setControlsAwake(false), CONTROLS_AWAKE_MS)
    }
  })
  // Native listeners on the canvas, not React's: the map is a node moved into its layer from elsewhere in React's tree,
  // so React's events from the map never pass through here. A touch wakes the controls on its press (a finger's moves are
  // drags); a mouse or pen by moving. A finger leaves as it lifts, so only a mouse leaving puts it away early.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const onDown = (event: PointerEvent) => wakeRef.current(event)
    const onMove = (event: PointerEvent) => {
      if (event.pointerType !== 'touch') wakeRef.current(event)
    }
    const onLeave = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return
      window.clearTimeout(controlsTimer.current)
      setControlsAwake(false)
    }
    root.addEventListener('pointerdown', onDown)
    root.addEventListener('pointermove', onMove)
    root.addEventListener('pointerleave', onLeave)
    return () => {
      root.removeEventListener('pointerdown', onDown)
      root.removeEventListener('pointermove', onMove)
      root.removeEventListener('pointerleave', onLeave)
      window.clearTimeout(controlsTimer.current)
    }
  }, [])
  const settledRef = useRef(onJumpSettled)
  useLayoutEffect(() => {
    settledRef.current = onJumpSettled
  })
  const settle = (which: CanvasJump) => {
    setSettledJump(which)
    settledRef.current?.(which)
  }
  // Settled: the camera fills the canvas and the map is in the card, out of reach, so focus left on it (the sphere that
  // opened) moves onto the swap button, the way back. After the commit: while the camera grows the button is hidden.
  const settledHere = jumped !== null && settledJump === jumped
  useLayoutEffect(() => {
    if (!settledHere) return
    const active = document.activeElement
    if (active === null || active === document.body || mapLayerRef.current?.contains(active)) swapRef.current?.focus({ preventScroll: true })
  }, [settledHere, jumped])
  useLayoutEffect(() => {
    if (!jumped) return
    const from = jumped.from
    const to = rootRef.current?.getBoundingClientRect()
    if (reducedMotion || !from || !to?.width || !to.height) {
      settle(jumped)
      return
    }
    const { x, y, scale } = growthFrom(from, to)
    setGrowth({
      jump: jumped,
      style: {
        '--watch-jump-x': `${x.toFixed(2)}px`,
        '--watch-jump-y': `${y.toFixed(2)}px`,
        '--watch-jump-scale': `${scale}`,
        '--watch-jump-radius': `${(from.radius / scale).toFixed(2)}px`,
      } as CSSProperties,
    })
  }, [jumped])
  // The camera grows for the theme's large duration (the animation's), then the jump settles.
  useEffect(() => {
    if (!grow || !jumped) return
    const timer = window.setTimeout(() => settle(jumped), largeDuration(rootRef.current))
    return () => window.clearTimeout(timer)
  }, [grow, jumped])

  // Escape goes back to the map after a jump, as the last thing the key is for: a window listener in the bubble phase,
  // so whatever uses it first (a zoomed face, StageFaces wherever focus is; an open menu or dialog; the chat) has had it
  // and marked it used. Not while focus is elsewhere (the chat's composer) or on a face, nor while a face shows zoomed
  // (it returns first, even if nothing marked the key).
  const swapBack = useRef(onSwap)
  useLayoutEffect(() => {
    swapBack.current = onSwap
  })
  const faceZoomed = () => Boolean(facesRef.current?.querySelector('[data-zoomed]'))
  useEffect(() => {
    if (!jumped) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented) return
      const active = document.activeElement
      const nowhere = active === null || active === document.body
      if (!nowhere && (!rootRef.current?.contains(active) || facesRef.current?.contains(active))) return
      if (faceZoomed()) return
      event.preventDefault()
      swapBack.current()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [jumped])
  // The swap button's own tooltip (open while it has keyboard focus, as after a jump from the keyboard) would take the
  // first press on the way back: Base UI's dismiss uses the key and stops it. This runs before that dismiss, so the
  // button goes back at once (the tooltip closes too), unless a face is zoomed or another popup is open (a menu
  // opened by hover), whose Escape it is.
  const onSwapKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (!jumped || event.key !== 'Escape' || event.nativeEvent.isComposing || event.defaultPrevented) return
    const button = event.currentTarget
    if (!button.hasAttribute('data-popup-open') || faceZoomed()) return
    if ([...document.querySelectorAll('[data-popup-open]')].some((open) => open !== button)) return
    event.preventDefault()
    swapBack.current()
  }

  const backButton = (
    <Button
      variant="secondary"
      elevated
      className="watch-canvas__back"
      aria-label={`Back to host, ${host.name}`}
      onClick={onBackToHost}
      onFocus={() => {
        backHasFocus.current = true
      }}
      onBlur={() => {
        backHasFocus.current = false
      }}
    >
      <ArrowLeft aria-hidden />
      <span className="watch-canvas__back-label">Back to host</span>
    </Button>
  )

  return (
    <div
      ref={rootRef}
      className={cn('watch-canvas', className)}
      data-mode={mode}
      data-swapped={swapped || undefined}
      data-jump={grow ? 'grow' : grown ? 'grown' : undefined}
      data-foot={footed || undefined}
      data-pip={pip ? true : undefined}
    >
      {(showCaption || showBack) && (
        <div className="watch-canvas__top">
          {showCaption && (
            <div className="watch-canvas__caption" data-slot="caption">
              {live && (
                <>
                  <Badge tone="live">Live</Badge>
                  <span className="watch-canvas__name">{watched.name}</span>
                  {sourceOff && (
                    <span className="watch-canvas__sound-off">
                      <VolumeOff aria-hidden="true" />
                      <span className="sr-only">Sound off by the host</span>
                    </span>
                  )}
                  {hostsPick && <HostAvatar host={host} size="xs" label="Host's pick" className="watch-host-mark" />}
                </>
              )}
              {label}
            </div>
          )}
          {showBack && backButton}
        </div>
      )}

      {broadcast && !pip && <div className="watch-canvas__broadcast">{broadcast}</div>}
      {reactions && <div className="watch-canvas__reactions">{reactions}</div>}

      {hasFaces && (
        <div ref={facesRef} className="watch-canvas__faces" data-slot="faces">
          {faces}
        </div>
      )}

      {hasControls && (
        <div ref={controlsRef} className="watch-canvas__controls" data-slot="controls" data-awake={controlsAwake || undefined}>
          {sound}
          {fullscreen}
        </div>
      )}

      <div className="watch-canvas__layer watch-canvas__layer--camera" data-slot={cameraSlot} style={grow}>
        <CameraPicture picture={pictureFor(watched, cameraCount, phase, connection)} slot={pip ? 'card' : cameraSlot} />
      </div>

      {pip && (
        <Tooltip content={pip.label}>
          <button type="button" className="watch-canvas__pip" aria-label={pip.label} onClick={pip.onOpen}>
            <span className="watch-canvas__swap-glyph" aria-hidden="true">
              <Maximize2 />
            </span>
          </button>
        </Tooltip>
      )}

      {hasCard && (
        <>
          <div ref={mapLayerRef} className="watch-canvas__layer watch-canvas__layer--map watch-map" data-slot={mapSlot}>
            {map}
          </div>

          {/* Over the card, not inside it, so it never wraps the map's own (inert) buttons and never remounts. */}
          <Tooltip content={swapLabel}>
            <button ref={swapRef} type="button" className="watch-canvas__swap" aria-label={swapLabel} onClick={onSwap} onKeyDown={onSwapKeyDown}>
              <span className="watch-canvas__swap-glyph" aria-hidden="true">
                <Maximize2 />
              </span>
              <CardLabel mode={mode} course={course} watched={watched} />
            </button>
          </Tooltip>
        </>
      )}
    </div>
  )
}

/**
 * What the swap card holds, bottom left, as the prototype's label: the course map (Course, or Map without a course) or
 * the watched camera by name (a Live show, a host's Studio camera or screen with its glyph, as the strip). Play Badge's overlay tone, decorative:
 * the swap button's name says the same. It names, never claims a state. While the card stands a glyph in for its
 * picture (a wait, offline, the map's pin) and on a card under 108px tall, watch.css takes it away: the glyph and the
 * button's name say it. With no camera to name (nothing to watch) the card has no label.
 */
function CardLabel({ mode, course, watched }: { mode: 'camera' | 'map'; course: boolean; watched: WatchCamera | null }) {
  if (mode === 'camera') {
    return (
      <Badge tone="overlay" icon={<MapIcon />} className="watch-canvas__card-label" aria-hidden="true">
        {course ? 'Course' : 'Map'}
      </Badge>
    )
  }
  if (!watched) return null
  return (
    <Badge
      tone="overlay"
      icon={watched.sourceRole === 'camera' ? <Video /> : <SourceGlyph role={watched.sourceRole} />}
      className="watch-canvas__card-label"
      aria-hidden="true"
    >
      {watched.name}
    </Badge>
  )
}

/** What the camera's layer shows: the watched camera's video, or why there is none. */
type Picture =
  | { kind: 'video'; identity: string }
  /** No camera can be watched and the room is connected: none on the event, warming up, a quiet stretch, or finished. */
  | { kind: 'quiet'; quiet: QuietCanvas }
  /** A camera that dropped: a Play EmptyState, its name and Offline. */
  | { kind: 'dropped'; name: string }
  /** The viewer's own connection is trying: the moving signal and its words, a status rather than a heading. */
  | { kind: 'trying'; words: string; motion: GlyphMotion }
  /** A camera on its way: the camera waking, its words for screen readers only. */
  | { kind: 'waking'; words: string }
  /** The event's video was refused: the crossed-out glyph alone, no words (the page's notice says why). */
  | { kind: 'refused' }

/**
 * Why the canvas shows what it shows. The glyph says whose the wait is: the viewer's connection is the signal on the
 * brand's stacked disc (tuning in, or tuning back in while it reconnects or a lost room tries again) and names no
 * camera; a camera on its way wakes on the stacked disc, wordless (LiveKit lists its video, the picture follows), never
 * Offline; a camera that isn't published is crossed out in the neutral disc, still, under its name, and says Offline
 * (the viewer can't tell whether it is trying again); with nothing to watch, the event has no cameras here, or the
 * brand mark warms up (a quiet stretch once the event has started) or settles once the scheduled end has passed.
 * Refused, nothing moves: the crossed-out glyph alone, and the page's notice says why.
 */
function pictureFor(watched: WatchCamera | null, cameraCount: CameraCount, phase: EventPhase, connection: CanvasConnection): Picture {
  if (connection === 'stopped') return { kind: 'refused' }
  if (connection !== 'connected') return { kind: 'trying', ...connectionWait(connection) }
  if (!watched) return { kind: 'quiet', quiet: quietCanvas(cameraCount, phase) }
  if (watched.online) return { kind: 'video', identity: watched.identity }
  if (watched.waiting) return { kind: 'waking', words: `${watched.name}, connecting` }
  return { kind: 'dropped', name: watched.name }
}

/**
 * In the swap card, beside the full-size picture: the glyph alone and still, its words for screen readers. A wait is the
 * stacked disc (the camera, or the signal), never the moving mark: the logo in the header, one moving mark on the
 * canvas.
 */
function cardArt(picture: Exclude<Picture, { kind: 'video' }>): { art: ReactElement; words?: string } {
  switch (picture.kind) {
    case 'quiet':
      return { art: <StackedGlyph icon={<Video />} />, words: quietWords(picture.quiet) }
    case 'trying':
      return { art: <StackedGlyph icon={<Wifi />} />, words: picture.words }
    case 'waking':
      return { art: <StackedGlyph icon={<Video />} />, words: picture.words }
    case 'dropped':
      return { art: <StateGlyph icon={<VideoOff />} />, words: `${picture.name}, offline` }
    case 'refused':
      return { art: <StateGlyph icon={<VideoOff />} /> }
  }
}

/**
 * The camera's layer: the video, or what stands in for it, centred over the canvas, never a picture. Full size, a quiet
 * canvas is the brand mark (moving) or the camera on the stacked disc (still, none here) over a compact Play
 * EmptyState's h3 in its inverse tone, under the Watch region's h2 (the page's), its plain meaning visually hidden at
 * the heading's start; a dropped camera is a compact inverse Play EmptyState; the connection's wait is the signal on
 * the stacked disc with its words as a status. In the swap card only the glyph shows, still (`cardArt`). Each is
 * announced when it changes, except the video refused (the page's notice says that).
 */
function CameraPicture({ picture, slot }: { picture: Picture; slot: Slot }) {
  if (picture.kind === 'video') {
    return (
      <LiveTrack
        identity={picture.identity}
        className="watch-canvas__video"
        fallback={
          <div className="watch-canvas__waiting">
            <StateGlyph icon={<VideoOff />} />
          </div>
        }
      />
    )
  }
  if (slot === 'card') {
    const { art, words } = cardArt(picture)
    return (
      <div className="watch-canvas__waiting" role={words ? 'status' : undefined}>
        {art}
        {words && <span className="sr-only">{words}</span>}
      </div>
    )
  }
  switch (picture.kind) {
    case 'quiet': {
      const { quiet } = picture
      const art = quiet.art === 'camera' ? <StackedGlyph icon={<Video />} /> : <WarmUpMark motion={quiet.art} className="watch-canvas__mark" />
      return <WaitingState className="watch-canvas__waiting" role="status" tone="inverse" art={art} title={quiet.title} plain={quiet.plain} />
    }
    case 'dropped':
      return (
        <div className="watch-canvas__waiting">
          <EmptyState role="status" tone="inverse" size="compact" headingLevel={3} icon={<VideoOff />} title={picture.name} description="Offline" />
        </div>
      )
    case 'trying':
      return (
        <div className="watch-canvas__waiting" role="status">
          <StackedGlyph icon={<Wifi />} motion={picture.motion} />
          <p className="watch-canvas__status">{picture.words}</p>
        </div>
      )
    case 'waking':
      return (
        <div className="watch-canvas__waiting" role="status">
          <StackedGlyph icon={<Video />} motion="wake" />
          <span className="sr-only">{picture.words}</span>
        </div>
      )
    case 'refused':
      return (
        <div className="watch-canvas__waiting">
          <StateGlyph icon={<VideoOff />} />
        </div>
      )
  }
}
